/**
 * Purchase requests (PSM buy_requests) for the signed-in customer.
 *
 * BFF: `GET /api/portal/buy-requests` (own rows only — customer_id scope or
 * the linked Customer Core UUID, so AUCNET STOCK requests are included) and
 * `GET /api/portal/buy-requests/:id` (ownership-checked, 404 otherwise).
 */
import { apiGet } from './api';

export interface PurchaseRequest {
  buyRequestId: number;
  stockVehicleId: string;
  state: string;
  offerAmount: number | null;
  deliveryCountryCode: string | null;
  deliveryPortCode: string | null;
  vehiclePriceSnapshot: number | null;
  vehicleStateSnapshot: string | null;
  vehicleImageUrlSnapshot: string | null;
  vehicleMakerEnSnapshot: string | null;
  vehicleCarnameEnSnapshot: string | null;
  vehicleGradeSnapshot: string | null;
  vehicleAuctionGradeSnapshot: string | null;
  vehicleInventoryNoSnapshot: string | null;
  source: string | null;
  stateUpdatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseRequestDetail extends PurchaseRequest {
  notes: string | null;
  rejectReason: string | null;
}

interface ListEnvelope {
  ok: true;
  data: { items: Partial<PurchaseRequest>[]; total: number; limit: number; offset: number };
}

interface DetailEnvelope {
  ok: true;
  data: Partial<PurchaseRequestDetail>;
}

/** Older BFF builds omit the snapshot fields; default them so the UI never reads undefined. */
function withDefaults<T extends Partial<PurchaseRequestDetail>>(row: T): PurchaseRequestDetail {
  return {
    buyRequestId: row.buyRequestId ?? 0,
    stockVehicleId: row.stockVehicleId ?? '',
    state: row.state ?? 'submitted',
    offerAmount: row.offerAmount ?? null,
    deliveryCountryCode: row.deliveryCountryCode ?? null,
    deliveryPortCode: row.deliveryPortCode ?? null,
    vehiclePriceSnapshot: row.vehiclePriceSnapshot ?? null,
    vehicleStateSnapshot: row.vehicleStateSnapshot ?? null,
    vehicleImageUrlSnapshot: row.vehicleImageUrlSnapshot ?? null,
    vehicleMakerEnSnapshot: row.vehicleMakerEnSnapshot ?? null,
    vehicleCarnameEnSnapshot: row.vehicleCarnameEnSnapshot ?? null,
    vehicleGradeSnapshot: row.vehicleGradeSnapshot ?? null,
    vehicleAuctionGradeSnapshot: row.vehicleAuctionGradeSnapshot ?? null,
    vehicleInventoryNoSnapshot: row.vehicleInventoryNoSnapshot ?? null,
    source: row.source ?? null,
    stateUpdatedAt: row.stateUpdatedAt ?? null,
    createdAt: row.createdAt ?? '',
    updatedAt: row.updatedAt ?? row.createdAt ?? '',
    notes: row.notes ?? null,
    rejectReason: row.rejectReason ?? null,
  };
}

const PAGE_SIZE = 100;
const MAX_PAGES = 20;

/** Every purchase request of the caller (paged through; the BFF caps limit at 100). */
export async function listPurchaseRequests(signal?: AbortSignal): Promise<PurchaseRequest[]> {
  const all: PurchaseRequest[] = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const res = await apiGet<ListEnvelope>(
      `/buy-requests?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`,
      signal,
    );
    const items = (res.data?.items ?? []).map(withDefaults).filter((r) => r.buyRequestId > 0);
    all.push(...items);
    if (items.length < PAGE_SIZE) break;
    if (all.length >= (res.data?.total ?? 0)) break;
  }
  return all;
}

export async function getPurchaseRequest(
  id: number,
  signal?: AbortSignal,
): Promise<PurchaseRequestDetail> {
  const res = await apiGet<DetailEnvelope>(`/buy-requests/${id}`, signal);
  return withDefaults(res.data ?? {});
}
